import { Injectable, inject } from '@angular/core';
import { CatalogueSearchItem, GroupNode, NodeTypes, SubNode } from '@app/shared/models/catalogue.model';
import { TopNodeType } from '@app/shared/models/salesbasket.enums';
import { Observable, defer, finalize, from, map, mergeMap, of, shareReplay, switchMap, tap, toArray } from 'rxjs';
import { ProductCatalogueApiService } from '../productcatalogue-api.service';
import { SalesBasketApiService } from '../salesbasket-api.service';

@Injectable({ providedIn: 'root' })
export class CatalogueService {
  private rootNodes = new Map<string, SubNode>();
  private subNodes = new Map<string, SubNode[]>();
  private searches = new Map<string, CatalogueSearchItem[]>();
  private pending = new Map<string, Observable<unknown>>();
  private selectableNodes = new Map<string, SubNode>();
  private imageUrls = new Map<string, string>();
  #api = inject(ProductCatalogueApiService);
  #salesBasketApi = inject(SalesBasketApiService);

  readonly NodeTypes = NodeTypes;

  clearCatalogue(): void {
    this.rootNodes = new Map();
    this.subNodes = new Map();
    this.searches = new Map();
    this.pending = new Map();
    this.selectableNodes = new Map();
    this.imageUrls = new Map();
  }

  getSingleNode(nodeId: string): SubNode | undefined {
    return this.selectableNodes.get(nodeId);
  }

  getRootCatalogueNode(rootNodeId: string): Observable<SubNode> {
    const nodes = this.selectableNodes;
    return this.cached(`root:${rootNodeId}`, this.rootNodes, rootNodeId, () =>
      this.#api.getRootCatalogueNode(rootNodeId).pipe(
        map(node => {
          if (!node?.Id) throw new Error('Katalogens rotkategori saknas.');
          return node as SubNode;
        }),
        tap(node => nodes.set(node.Id, node))
      ));
  }

  getSubNodes(rootNodeId: string, nodeId: string): Observable<SubNode[]> {
    // The same group can have different contents under different roots.
    const key = JSON.stringify([rootNodeId, nodeId]);
    const nodes = this.selectableNodes;
    return this.cached(`children:${key}`, this.subNodes, key, () =>
      this.#api.getSubCatalogueNodes(rootNodeId, nodeId).pipe(
        map(result => (result ?? []) as SubNode[]),
        tap(result => result.forEach(node => nodes.set(node.Id, node)))
      ));
  }

  loadSearchableItems(root: SubNode): Observable<CatalogueSearchItem[]> {
    return this.cached(`search:${root.Id}`, this.searches, root.Id, () =>
      this.searchBranch(root.Id, [new GroupNode({
        GroupId: root.Id, GroupName: root.Name,
        SelectedNodeId: root.Id, SelectedNodeName: root.Name
      })])
    );
  }

  private searchBranch(rootId: string, path: GroupNode[]): Observable<CatalogueSearchItem[]> {
    return this.getSubNodes(rootId, path[path.length - 1].GroupId).pipe(
      switchMap(nodes => from(nodes).pipe(
        mergeMap(node => {
          if (node.Type === NodeTypes.Item) {
            const groups = path.map(group => new GroupNode(group));
            return of([{ label: node.Name, value: node.ItemId, node, groupNodes: groups }]);
          }
          if ((node.Type !== NodeTypes.Group && node.Type !== NodeTypes.Root) ||
              path.some(group => group.GroupId === node.Id)) return of([]);
          const groups = path.map(group => new GroupNode(group));
          groups.push(new GroupNode({
            GroupId: node.Id, GroupName: node.Name,
            SelectedNodeId: node.Id, SelectedNodeName: node.Name, ListIndex: groups.length
          }));
          return this.searchBranch(rootId, groups);
        }, 2),
        toArray(),
        map(branches => branches.flat())
      ))
    );
  }

  private cached<T>(requestKey: string, cache: Map<string, T>, key: string,
                    fetch: () => Observable<T>): Observable<T> {
    const pending = this.pending;
    return defer(() => {
      if (cache.has(key)) return of(cache.get(key)!);
      const existing = pending.get(requestKey);
      if (existing) return existing as Observable<T>;
      const request = defer(fetch).pipe(
        tap(value => cache.set(key, value)),
        finalize(() => pending.delete(requestKey)),
        shareReplay({ bufferSize: 1, refCount: true })
      );
      pending.set(requestKey, request);
      return request;
    });
  }

  createSalesBasket(brand: string, nodeType?: TopNodeType): Observable<any> {
    return this.#salesBasketApi.createSalesBasket(brand, nodeType);
  }

  getCatalogueImageUrl(nodeId: string, imageFilepath?: string, imageFilename?: string): string {
    const key = JSON.stringify([nodeId, imageFilepath, imageFilename]);
    if (!this.imageUrls.has(key)) {
      this.imageUrls.set(key, this.#api.getCatalogueItemImageUrl(imageFilename, imageFilepath).URL);
    }
    return this.imageUrls.get(key)!;
  }
}
